/**
 * Proctor flags for one sitting, as a stacked bar on a scale shared with every
 * other row in the table.
 *
 * The counts are already in the row as numbers; this exists so a table of
 * numbers can be *scanned* — which attempt was the noisy one is a shape
 * question, not an arithmetic one. Severity is encoded three ways at once
 * (order, fill and a hatch/dot pattern) because the palette here is red, amber
 * and grey, and red-green-blind readers must still be able to tell a critical
 * segment from a warning one. The exact numbers stay in the adjacent cells; the
 * bar never replaces them.
 */
import { useId } from "react";
import type { SeverityCounts } from "../data/types";

const WIDTH = 120;
const HEIGHT = 8;

export function ChartSeverityBar({
  severity,
  /** Largest total in the table, so every row is drawn on one scale. */
  max,
}: {
  severity: SeverityCounts;
  max: number;
}) {
  const id = useId();
  const total = severity.critical + severity.warn + severity.info;

  if (total === 0 || max === 0) {
    return <span className="text-2xs text-ink-faint">none</span>;
  }

  const scale = (n: number) => (n / max) * WIDTH;
  const criticalW = scale(severity.critical);
  const warnW = scale(severity.warn);
  const infoW = scale(severity.info);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-30"
      role="img"
      aria-label={`${severity.critical} critical, ${severity.warn} warning, ${severity.info} info flags out of a table maximum of ${max}`}
    >
      <title>
        {`${severity.critical} critical, ${severity.warn} warning, ${severity.info} info`}
      </title>
      <defs>
        {/* Hatch for critical, dots for warning: the pattern, not the hue, is
            what a reader who cannot separate red from amber goes by. */}
        <pattern
          id={`${id}-critical`}
          width={4}
          height={4}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width={4} height={4} fill="var(--color-violation-soft)" />
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={4}
            stroke="var(--color-violation)"
            strokeWidth={2}
          />
        </pattern>
        <pattern id={`${id}-warn`} width={4} height={4} patternUnits="userSpaceOnUse">
          <rect width={4} height={4} fill="var(--color-review-soft)" />
          <circle cx={2} cy={2} r={1.1} fill="var(--color-review)" />
        </pattern>
      </defs>

      <rect x={0} y={0} width={WIDTH} height={HEIGHT} fill="var(--color-surface-sunken)" />
      {severity.critical > 0 ? (
        <rect x={0} y={0} width={criticalW} height={HEIGHT} fill={`url(#${id}-critical)`} />
      ) : null}
      {severity.warn > 0 ? (
        <rect
          x={criticalW}
          y={0}
          width={warnW}
          height={HEIGHT}
          fill={`url(#${id}-warn)`}
        />
      ) : null}
      {severity.info > 0 ? (
        <rect
          x={criticalW + warnW}
          y={0}
          width={infoW}
          height={HEIGHT}
          fill="var(--color-border-strong)"
        />
      ) : null}
    </svg>
  );
}

/** The key for the bar above. Rendered once per table, not once per row. */
export function ChartSeverityLegend() {
  return (
    <p className="text-2xs text-ink-faint">
      Bar: <span className="font-medium text-violation">hatched = critical</span>,{" "}
      <span className="font-medium text-review">dotted = warning</span>, solid grey =
      info. All three are on one scale across the table.
    </p>
  );
}
